import { storage } from './storage';

// Access verdict cache for fleet web. The allowlist lives on the backend and is
// never downloaded; we ask `POST /v1/fleet/auth/whitelist/check { email }` and
// cache the per-email yes/no so login still works (fail-closed) during a brief
// backend outage.
//
//   login ──check(email)──▶ success → setCachedVerdict(email, allowed)
//                           failure → getCachedVerdict(email)
//                                     (unknown → caller blocks login)
//
// NOTE: the cache key is intentionally NOT in storage's AUTH_KEYS, so it
// survives sign-out (storage.clear() only wipes token + profile).

const CACHE_KEY = 'access_verdicts';

const normalize = (email: string | null | undefined): string =>
  (email || '').trim().toLowerCase();

type VerdictMap = Record<string, boolean>;

const readMap = (): VerdictMap => {
  try {
    const raw = storage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as VerdictMap) : {};
  } catch {
    return {};
  }
};

/**
 * Cached access verdict for an email:
 *   true  → known allowed
 *   false → known denied
 *   null  → unknown (never checked, or cache cleared)
 * Callers must treat `null` as "cannot verify" and fail closed.
 */
export const getCachedVerdict = (
  email: string | null | undefined
): boolean | null => {
  const key = normalize(email);
  if (!key) return null;
  const map = readMap();
  return key in map ? map[key] : null;
};

/** Persist the latest verdict for an email. Best-effort. */
export const setCachedVerdict = (
  email: string | null | undefined,
  allowed: boolean
): void => {
  const key = normalize(email);
  if (!key) return;
  try {
    const map = readMap();
    map[key] = allowed;
    storage.setItem(CACHE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / serialization errors — the cache is best-effort
  }
};
